// Página OfferDetail - Detalle y confirmación de oferta
// Permite ver el detalle de una oferta y confirmarla para impactar precios

import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useOffer, useOfferOperations } from '../../hooks/useOffers'
import { useAuth } from '../../hooks/useAuth'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import ConfirmDialog from '../../components/ConfirmDialog'
import { formatDate, formatCurrency } from '../../utils/helpers'
import './OfferDetail.css'

/**
 * Página OfferDetail
 * Muestra el detalle de una oferta y permite confirmarla
 */
function OfferDetail() {
  const { id } = useParams()
  const { userData } = useAuth()
  const { offer, loading, error, refetch } = useOffer(id)
  const { confirm, deactivate, loading: operationLoading } = useOfferOperations()

  const [confirmDialog, setConfirmDialog] = useState(false)
  const [deactivateDialog, setDeactivateDialog] = useState(false)
  const [notification, setNotification] = useState(null)

  // Verificar si la oferta está activa (dentro de fechas)
  const isOfferActive = () => {
    if (!offer || !offer.active || !offer.confirmed) return false
    const now = new Date()
    const startDate = offer.startDate?.toDate ? offer.startDate.toDate() : new Date(offer.startDate)
    const endDate = offer.endDate?.toDate ? offer.endDate.toDate() : new Date(offer.endDate)
    return now >= startDate && now <= endDate
  }

  // Manejar confirmación de oferta
  const handleConfirm = async () => {
    try {
      await confirm(id, userData?.uid, userData?.name || userData?.displayName || 'Sistema')
      setConfirmDialog(false)
      setNotification({
        type: 'success',
        message: 'Oferta confirmada correctamente. Los precios especiales han sido aplicados.'
      })
      setTimeout(() => setNotification(null), 5000)
      refetch()
    } catch (err) {
      setNotification({
        type: 'error',
        message: err.message || 'Error al confirmar la oferta'
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  // Manejar desactivación de oferta
  const handleDeactivate = async () => {
    try {
      await deactivate(id)
      setDeactivateDialog(false)
      setNotification({
        type: 'success',
        message: 'Oferta desactivada correctamente. Los precios especiales han sido limpiados.'
      })
      setTimeout(() => setNotification(null), 5000)
      refetch()
    } catch (err) {
      setNotification({
        type: 'error',
        message: err.message || 'Error al desactivar la oferta'
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  // Agrupar productos por categoría
  const productsByCategory = () => {
    if (!offer || !offer.products) return {}
    const grouped = {}
    offer.products.forEach(product => {
      const categoryName = product.categoryName || 'Sin categoría'
      if (!grouped[categoryName]) {
        grouped[categoryName] = []
      }
      grouped[categoryName].push(product)
    })
    return grouped
  }

  // Calcular estadísticas
  const stats = () => {
    if (!offer || !offer.products) return { total: 0, included: 0, excluded: 0, totalDiscount: 0 }
    
    const included = offer.products.filter(p => !p.excluded)
    const totalDiscount = included.reduce((sum, p) => {
      return sum + (p.originalPrice - p.finalPrice)
    }, 0)

    return {
      total: offer.products.length,
      included: included.length,
      excluded: offer.products.length - included.length,
      totalDiscount
    }
  }

  if (loading) {
    return (
      <div className="offer-detail-page">
        <div className="loading-state">
          <p>Cargando oferta...</p>
        </div>
      </div>
    )
  }

  if (error || !offer) {
    return (
      <div className="offer-detail-page">
        <div className="error-state">
          <p>Error: {error || 'Oferta no encontrada'}</p>
          <Link to="/admin/ofertas">
            <Button>Volver a Ofertas</Button>
          </Link>
        </div>
      </div>
    )
  }

  const groupedProducts = productsByCategory()
  const offerStats = stats()
  const active = isOfferActive()

  return (
    <div className="offer-detail-page">
      <div className="page-header">
        <div>
          <h1>{offer.name || 'Oferta sin nombre'}</h1>
          <p>Número: {offer.offerNumber || '-'}</p>
        </div>
        <div className="header-actions">
          {!offer.confirmed && (
            <Link to={`/admin/ofertas/${id}/editar`}>
              <Button variant="outline">✏️ Editar</Button>
            </Link>
          )}
          <Link to="/admin/ofertas">
            <Button variant="outline">← Volver</Button>
          </Link>
        </div>
      </div>

      {/* Información General */}
      <div className="info-card">
        <h3>Información General</h3>
        <div className="info-grid">
          <div className="info-item">
            <label>Estado:</label>
            <span className={`status-badge ${active ? 'active' : 'inactive'}`}>
              {active ? '🟢 Activa' : offer.confirmed ? '✅ Confirmada' : '⏸️ Inactiva'}
            </span>
          </div>
          <div className="info-item">
            <label>Confirmada:</label>
            <span className={offer.confirmed ? 'confirmed-badge' : 'pending-badge'}>
              {offer.confirmed ? '✓ Sí' : '⏳ No'}
            </span>
          </div>
          <div className="info-item">
            <label>Fecha de Inicio:</label>
            <span>{formatDate(offer.startDate)}</span>
          </div>
          <div className="info-item">
            <label>Fecha de Fin:</label>
            <span>{formatDate(offer.endDate)}</span>
          </div>
          <div className="info-item">
            <label>Creada por:</label>
            <span>{offer.createdByName || 'Sistema'}</span>
          </div>
          {offer.confirmed && offer.confirmedAt && (
            <div className="info-item">
              <label>Confirmada por:</label>
              <span>{offer.confirmedByName || 'Sistema'}</span>
            </div>
          )}
        </div>
      </div>

      {/* Estadísticas */}
      <div className="stats-card">
        <h3>Estadísticas</h3>
        <div className="stats-grid">
          <div className="stat-item">
            <div className="stat-value">{offerStats.total}</div>
            <div className="stat-label">Total Productos</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{offerStats.included}</div>
            <div className="stat-label">Incluidos</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{offerStats.excluded}</div>
            <div className="stat-label">Excluidos</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{formatCurrency(offerStats.totalDiscount)}</div>
            <div className="stat-label">Descuento Total</div>
          </div>
        </div>
      </div>

      {/* Productos por Categoría */}
      <div className="products-card">
        <h3>Productos de la Oferta</h3>
        {Object.keys(groupedProducts).length > 0 ? (
          Object.entries(groupedProducts).map(([categoryName, products]) => (
            <div key={categoryName} className="category-section">
              <h4 className="category-title">{categoryName}</h4>
              <div className="products-table-container">
                <table className="products-table">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Precio Original</th>
                      <th>Descuento</th>
                      <th>Precio Final</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => {
                      const discount = product.originalPrice - product.finalPrice
                      const discountPercent = product.originalPrice > 0
                        ? ((discount / product.originalPrice) * 100).toFixed(1)
                        : 0

                      return (
                        <tr key={product.productId} className={product.excluded ? 'excluded-row' : ''}>
                          <td>
                            <strong>{product.productName}</strong>
                          </td>
                          <td>{formatCurrency(product.originalPrice)}</td>
                          <td>
                            {product.discountType === 'percentage' ? (
                              <span className="discount-badge">{product.discountValue}%</span>
                            ) : (
                              <span className="discount-badge">{formatCurrency(product.discountValue)}</span>
                            )}
                            <small className="discount-percent">({discountPercent}%)</small>
                          </td>
                          <td>
                            <strong className="final-price">{formatCurrency(product.finalPrice)}</strong>
                          </td>
                          <td>
                            {product.excluded ? (
                              <span className="excluded-badge">Excluido</span>
                            ) : (
                              <span className="included-badge">Incluido</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        ) : (
          <p className="no-products">No hay productos en esta oferta</p>
        )}
      </div>

      {/* Acciones */}
      {!offer.confirmed && (
        <div className="actions-card">
          <h3>Confirmar Oferta</h3>
          <p>
            Al confirmar esta oferta, se aplicarán los precios especiales a todos los productos incluidos.
            Esta acción no se puede deshacer fácilmente.
          </p>
          <Button
            variant="primary"
            onClick={() => setConfirmDialog(true)}
            disabled={operationLoading}
          >
            ✓ Confirmar Oferta
          </Button>
        </div>
      )}

      {offer.confirmed && offer.active && (
        <div className="actions-card">
          <h3>Desactivar Oferta</h3>
          <p>
            Al desactivar esta oferta, se limpiarán los precios especiales de todos los productos afectados,
            volviendo a sus precios originales.
          </p>
          <Button
            variant="danger"
            onClick={() => setDeactivateDialog(true)}
            disabled={operationLoading}
          >
            ⏸️ Desactivar Oferta
          </Button>
        </div>
      )}

      {/* Diálogos de confirmación */}
      <ConfirmDialog
        isOpen={confirmDialog}
        onClose={() => setConfirmDialog(false)}
        onConfirm={handleConfirm}
        title="Confirmar Oferta"
        message={
          <div>
            <p>¿Estás seguro de confirmar esta oferta?</p>
            <p><strong>Esta acción aplicará los precios especiales a {offerStats.included} productos.</strong></p>
            <p>El descuento total será de {formatCurrency(offerStats.totalDiscount)}.</p>
            <p className="warning-text">⚠️ Esta acción no se puede deshacer fácilmente.</p>
          </div>
        }
        confirmText="Confirmar"
        cancelText="Cancelar"
        variant="warning"
      />

      <ConfirmDialog
        isOpen={deactivateDialog}
        onClose={() => setDeactivateDialog(false)}
        onConfirm={handleDeactivate}
        title="Desactivar Oferta"
        message={
          <div>
            <p>¿Estás seguro de desactivar esta oferta?</p>
            <p><strong>Se limpiarán los precios especiales de {offerStats.included} productos.</strong></p>
            <p>Los productos volverán a sus precios originales.</p>
          </div>
        }
        confirmText="Desactivar"
        cancelText="Cancelar"
        variant="warning"
      />

      {/* Notificaciones */}
      {notification && (
        <Toast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  )
}

export default OfferDetail

